import {
  ColorPicker,
  FormLabel,
  FormTextInput,
  FullScreenLoadingSpinner,
  RadioButtons,
  SingleSelectDropdown,
  SubTitle,
  TargetGroupDropdownSelector,
} from 'ck-components';
import { QK, ThemeFormData, useEventsIdName } from 'ck-queries';
import { useEffect, useMemo } from 'react';

import { TargetAudienceCount } from 'src/components/TargetAudienceCount';
import { TargetDivisionDropdownSelector } from 'src/components/Form/TargetDivisionDropdownSelector/TargetDivisionDropdownSelector';
import { UseFormReturn } from 'react-hook-form';
import dayjs from 'dayjs';
import { useQueryTargetGroupsAudienceCount } from 'src/queries/targetGroups/useQueryTargetGroupsAudience';

interface Props {
  form: UseFormReturn<ThemeFormData>;
}

export const TemaSection = ({ form }: Props) => {
  const Q_EventsIdName = useEventsIdName();
  const eventsIdName = useMemo(
    () => Q_EventsIdName.data ?? [],
    [Q_EventsIdName.data]
  );

  const themeLinkButtonType = form.watch('themeLinkButtonType');
  const end = form.watch('end');
  const start = form.watch('start');
  const division = form.watch('targetGroup.divisionName');
  const targetGroup = form.watch('targetGroup.group');

  const Q_TargetGroupsAudienceCount =
    useQueryTargetGroupsAudienceCount(division);

  // Refetch audience count when division changes
  useEffect(() => {
    Q_TargetGroupsAudienceCount.refetch({
      queryKey: [QK.targetGroupsAudienceCount, division],
    });
  }, [division]);

  // Update Count based on selected target group
  const count = useMemo(() => {
    const data = Q_TargetGroupsAudienceCount.data;
    if (!data) return 0;

    const newCount =
      data.find(({ group }) => group === targetGroup)?.count ?? 0;
    form.setValue('targetGroup.count', newCount);
    return newCount;
  }, [Q_TargetGroupsAudienceCount.data, targetGroup, division]);

  if (Q_EventsIdName.isLoading || !Q_EventsIdName.data) {
    return <FullScreenLoadingSpinner />;
  }

  return (
    <div className=''>
      <SubTitle label='1. Tema' />
      <div>
        <FormTextInput
          label={'NAMNGE TEMA'}
          name='title'
          parentForm={form}
          maxLength={40}
        />
        <TargetGroupDropdownSelector name='targetGroup.group' form={form} />
        <div className='mb-10'>
          {/* Uncomment when we want to specify division
          <TargetDivisionDropdownSelector
            name='targetGroup.divisionName'
            form={form}
          /> */}
          <TargetAudienceCount count={count} />
        </div>
        <TargetDivisionDropdownSelector
          label='Avsändare / Sender'
          name='createdByDivisionName'
          form={form}
        />
      </div>
      <div className='flex flex-col gap-4 md:flex-row md:items-center'>
        <FormTextInput
          label={'START'}
          parentForm={form}
          name='start'
          type='datetime-local'
          minDate={dayjs().format('YYYY-MM-DDTHH:mm')}
          options={{
            validate: {
              startIsInFuture: (value) =>
                dayjs().isBefore(value) || 'Startdatum måste vara i framtiden',
              startIsBeforeEnd: (value) =>
                dayjs(value).isBefore(dayjs(end)) ||
                'Startdatum måste vara innan slutdatum',
            },
          }}
        />
        <div className='hidden text-2xl font-thin md:block'>-</div>
        <FormTextInput
          label={'SLUT'}
          parentForm={form}
          name='end'
          type='datetime-local'
          minDate={dayjs().format(start)}
          options={{
            validate: (value) =>
              dayjs(value).isAfter(start) ||
              'Slutdatum måste vara efter startdatum',
          }}
        />
      </div>
      <FormLabel label='KNAPP STARTSIDA'>
        <div className='mb-6'>
          <RadioButtons
            name='themeLinkButtonType'
            parentForm={form}
            onChange={(e) => {
              switch (e.target.value) {
                case 'none': {
                  form.unregister('themeLinkButtonText');
                  form.unregister('themeLinkUrl');
                  form.unregister('themeButtonEventId');
                  form.unregister('theme.palette.section.home.themeLinkButton');
                  break;
                }
                case 'in-app': {
                  form.unregister('themeLinkUrl');
                  break;
                }
                case 'URL': {
                  form.unregister('themeButtonEventId');
                  break;
                }
              }
            }}
            selectOptions={[
              { label: 'Ingen knapp', value: 'none' },
              { label: 'Öppna event i appen', value: 'in-app' },
              { label: 'Öppna URL i webbläsare', value: 'url' },
            ]}
          />
        </div>
      </FormLabel>
      {themeLinkButtonType !== 'none' && (
        <FormTextInput
          label={'KNAPPTEXT'}
          parentForm={form}
          name='themeLinkButtonText'
          type='textfield'
          maxLength={22}
        />
      )}
      {themeLinkButtonType === 'in-app' && (
        <SingleSelectDropdown
          label='VÄLJ EVENT'
          description='Välj event'
          name='themeButtonEventId'
          form={form}
          values={eventsIdName.map(({ id }) => id)}
          labelFromValue={(value: string) =>
            eventsIdName.find((d) => d.id === value)?.name ?? ''
          }
        />
      )}
      {themeLinkButtonType === 'url' && (
        <FormTextInput
          label={'LÄNK'}
          parentForm={form}
          name='themeLinkUrl'
          type='textfield'
        />
      )}
      {themeLinkButtonType !== 'none' && (
        <ColorPicker
          label='KNAPP STARTSIDA'
          name='theme.palette.section.home.themeLinkButton'
          form={form}
        />
      )}
    </div>
  );
};
